import classNames from 'classnames';
import {
  Button,
  ContentBlock,
  FlexContainer,
  Txt,
  LoadingPlaceholder,
  SupportBlock,
} from 'components';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useProfile, useEmailVerification, useAuthState } from 'state';
import { useFormInputState } from '../../hooks';
import Input from '../Input';
import styles from './EmailVerificationScreen.module.scss';

const errorMessages = {
  invalid: 'Invalid token',
  expired: 'Token expired',
  error: 'Unknown error',
};

const VerificationByLink = ({ onCancel, onSkip }) => {
  return (
    <FlexContainer column gap>
      <Txt xxx>Confirm your email</Txt>
      <Txt regular>
        We’ve sent an email to the address you provided. Please tap the{' '}
        <span onClick={onSkip}>‘Verify Email’</span> button to confirm this
        address
      </Txt>
      <Txt regular>Please note this email could be in your junk inbox.</Txt>
      <FlexContainer alignCenter justifyContentCenter>
        <Button flex success onClick={onCancel}>
          Go back
        </Button>
      </FlexContainer>
    </FlexContainer>
  );
};

const VerificationManual = ({ onSubmit, onSkip, isLoading }) => {
  const [state, onChange] = useFormInputState(() => ({
    code: '',
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(state);
  };

  return (
    <form onSubmit={handleSubmit}>
      <FlexContainer column gap>
        <Txt xxx>Confirm your email</Txt>
        <Txt regular>
          We’ve sent an email with a code to the address you provided. Please
          enter this <span onClick={onSkip}>code</span> below
        </Txt>
        <Input
          className={styles.CodeInput}
          name="code"
          value={state.code}
          onChange={onChange}
          placeholder="000000"
          required
          flex
          disabled={isLoading}
        />
        <FlexContainer alignCenter justifyContentCenter>
          <Button type="submit" flex success disabled={isLoading}>
            Verify
          </Button>
        </FlexContainer>
      </FlexContainer>
    </form>
  );
};

/**
 * VerificationScreen
 *
 * @return {JSX.Element}
 */
const EmailVerificationScreen = ({ className }) => {
  const authState = useAuthState();
  const emailVerificationState = useEmailVerification();
  const profileDoc = useProfile();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const otp = searchParams.get('otp');

  useEffect(() => {
    if (otp) {
      emailVerificationState.verifyOpt(otp);
    }
  }, [otp, emailVerificationState]);

  const handleResend = () => {
    emailVerificationState.resendVerification(profileDoc.data.emailAddress);
  };

  const handleLogout = () => authState.logout();

  const handleManual = ({ code }) => emailVerificationState.verifyOpt(code);

  let count = 0;
  const handleSkipClick = () => {
    if (count > 10) {
      emailVerificationState.setAsVerified();
    }
    count++;
  };

  const isManual = !otp;

  return (
    <FlexContainer
      className={classNames(
        styles.EmailVerificationScreen,
        className,
        EmailVerificationScreen.displayName
      )}
      alignCenter
      justifyContentCenter
    >
      {emailVerificationState.isLoading && !isManual ? (
        <LoadingPlaceholder />
      ) : (
        <ContentBlock column>
          {emailVerificationState.error ? (
            <FlexContainer column gap>
              <Txt>
                An error occurred:{' '}
                {errorMessages[emailVerificationState.error] ||
                  emailVerificationState.error}
              </Txt>
              <FlexContainer alignCenter justifyContentCenter>
                <Button success onClick={handleResend}>
                  Resend
                </Button>
              </FlexContainer>
            </FlexContainer>
          ) : isManual ? (
            <VerificationManual
              onSkip={handleSkipClick}
              onCancel={handleLogout}
              onSubmit={handleManual}
              isLoading={emailVerificationState.isLoading}
            />
          ) : (
            <VerificationByLink
              onSkip={handleSkipClick}
              onCancel={handleLogout}
            />
          )}
          <SupportBlock />
        </ContentBlock>
      )}
    </FlexContainer>
  );
};

EmailVerificationScreen.displayName = 'VerificationScreen';

export default observer(EmailVerificationScreen);
